import { FC, ReactElement, ReactNode } from 'react';

import * as amplitude from '@amplitude/analytics-browser';
import { Box, Grid, Stack, SxProps, Typography } from '@mui/material';
import {
  ArrowFatLineDown,
  ArrowFatLineUp,
  Bank,
  Books,
  DiscordLogo,
  MediumLogo,
  TwitterLogo,
} from 'phosphor-react';

import { useDashboardStats } from '~components/hooks/dashboard/useDashboardStats';
import useAssetPrices from '~components/hooks/protocol/assets/useAssetPrices';
import { useAllVaultData } from '~components/hooks/protocol/vaults/useAllVaultData';
import { useIsMobile } from '~components/hooks/utils/useIsMobile';
import { NormalLink } from '~components/shared/link';
import { black, primaryColors, secondaryColors } from '~components/theme/styles/colors';
import { columnGap } from '~components/theme/styles/mixins';
import { EDisplayType } from '~components/types';
import { displayFormatUnits } from '~components/utils/utils';
import { ENativeAsset } from '~config/types';

import { GenericInfoLabel } from '../../ui/label';
import { LoadSkeleton } from '../../ui/loadSkeleton';

const SystemStatisticsItem: React.FC<{
  stat: ReactNode;
  isLoading: boolean;
  isIncrease?: boolean;
  children?: ReactNode;
}> = ({ stat, isLoading, isIncrease, children }) => (
  <Grid>
    <GenericInfoLabel
      sx={{ flexDirection: 'row-reverse', minWidth: 0, justifyContent: 'flex-end', mb: 0.5 }}
      toolTip={{ content: 'Lorem ipsum', placement: 'title' }}
      labelTitle={
        <Stack direction={'row'} alignItems={'center'} spacing={1}>
          <Typography variant={'label3'} color={primaryColors[700]}>
            {stat}
          </Typography>
          {typeof isIncrease === 'boolean' && (
            <Grid sx={{ color: secondaryColors[200], lineHeight: '12px' }}>
              {isIncrease ? <ArrowFatLineUp size={12} /> : <ArrowFatLineDown size={12} />}
            </Grid>
          )}
        </Stack>
      }
    />

    <LoadSkeleton isLoading={isLoading}>
      <Typography variant="lead" sx={{ color: primaryColors[100] }}>
        {children}
      </Typography>
    </LoadSkeleton>
  </Grid>
);

const SocialLink: FC<{ href: string; icon: ReactElement; label: string }> = ({ href, icon }) => {
  return (
    <NormalLink
      onClick={() => {
        amplitude.track('footer-link', { type: href });
      }}
      href={href}
      target={'_blank'}
      sx={{ textDecoration: 'none', '& :hover': { color: 'white' } }}
    >
      <Stack direction={'row'} alignItems={'center'} spacing={2}>
        {icon}
      </Stack>
    </NormalLink>
  );
};

export const Footer = () => {
  const { computed } = useAllVaultData();
  const {
    data: {
      market: { VST: vstPrice, VSTA: vstaPrice },
    },
    isLoading: priceLoading,
  } = useAssetPrices();

  const isMobile = useIsMobile();

  const { data: stats = [], isLoading: isDashLoading } = useDashboardStats('Week');
  const isTvlIncrease =
    (computed?.totalCollateralUsd ?? 0) > (Number(stats[stats.length - 1]?.stats?.tvl.tvlUsd) || 0);

  const systemStatistics: {
    stat: string;
    isLoading: boolean;
    children: ReactNode;
    isIncrease?: boolean;
  }[] = [
    {
      stat: ENativeAsset.VSTA,
      isLoading: priceLoading,
      children: displayFormatUnits({ value: vstaPrice, displayType: EDisplayType.USD, decimal: 3 }),
    },
    {
      stat: ENativeAsset.VST,
      isLoading: priceLoading,
      children: displayFormatUnits({ value: vstPrice, displayType: EDisplayType.USD, decimal: 3 }),
    },
  ];

  if (!isMobile) {
    systemStatistics.push({
      stat: 'TOTAL VALUE LOCKED',
      isLoading: !!computed?.isAnyVaultLoading || isDashLoading,
      children: displayFormatUnits({
        value: computed?.totalCollateralUsd ?? 0,
        displayType: EDisplayType.USD,
      }),
      isIncrease: isTvlIncrease,
    });
  }

  return (
    <Box sx={{ padding: ['32px', '48px 64px'], background: black['15%'], width: '100%' }}>
      <Grid container sx={{ alignItems: 'center', justifyContent: 'space-between', gap: '32px' }}>
        <Grid
          sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexGrow: [1, 0.6] }}
        >
          {systemStatistics.map(({ stat, isLoading, isIncrease, children }) => (
            <SystemStatisticsItem stat={stat} isLoading={isLoading} key={stat} isIncrease={isIncrease}>
              {children}
            </SystemStatisticsItem>
          ))}
        </Grid>
        <Grid sx={{ ...columnGap(8), width: ['100%', 'fit-content'] } as SxProps}>
          <Typography variant="label3" sx={{ color: primaryColors[700], textAlign: ['left', 'end'] }}>
            STAY IN THE LOOP
          </Typography>
          <Grid
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: '32px',
              justifyContent: ['space-between', undefined],
            }}
          >
            <SocialLink
              label="Participate in governance"
              href="https://curia.vestafinance.xyz/"
              icon={<Bank size={24} color={primaryColors[400]} weight="regular" />}
            />
            <SocialLink
              label="Docs & Whitepaper"
              href="https://docs.vestafinance.xyz/"
              icon={<Books size={24} color={primaryColors[400]} weight="regular" />}
            />
            <SocialLink
              label="Join our Discord Community!"
              href="https://discord.gg/yNc6Cm638"
              icon={<DiscordLogo size={24} color={primaryColors[400]} weight="fill" />}
            />
            <SocialLink
              label="Follow us on Twitter!"
              href="https://twitter.com/vestafinance"
              icon={<TwitterLogo size={24} color={primaryColors[400]} weight="fill" />}
            />
            <SocialLink
              label="Subscribe to our Medium!"
              href="https://medium.com/@VestaFinance"
              icon={<MediumLogo size={24} color={primaryColors[400]} weight="fill" />}
            />
          </Grid>
        </Grid>
      </Grid>
    </Box>
  );
};
